import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import { useCart } from '../contexts/CartContext';
import { categoryService, productService, brandService } from '../services';
import { gtagService } from '../services/gtagService';
import type { FilterOptions } from '../services/types';
import type { Category, Product } from '../types/Product';

interface FilterState {
  category: string[];
  brand: string[];
  priceRange: [number, number];
  inStock: boolean | null;
  tags: string[];
  searchQuery: string;
  sortBy: 'name' | 'price-low' | 'price-high' | 'newest' | 'featured';
}

const Products: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { addToCart } = useCart();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [availableBrands, setAvailableBrands] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const itemsPerPage = 100; // Default to 100 items per page
  
  const [filters, setFilters] = useState<FilterState>({
    category: searchParams.getAll('category') || [],
    brand: searchParams.getAll('brand') || [],
    priceRange: [
      parseInt(searchParams.get('minPrice') || '0') || 0,
      parseInt(searchParams.get('maxPrice') || '25000') || 25000
    ],
    inStock: searchParams.get('inStock') === 'true' ? true : searchParams.get('inStock') === 'false' ? false : null,
    tags: searchParams.getAll('tags') || [],
    searchQuery: searchParams.get('search') || '',
    sortBy: (searchParams.get('sort') as FilterState['sortBy']) || 'featured',
  });

  // Load categories and brands
  useEffect(() => {
    let isMounted = true;
    const loadCategoriesAndBrands = async () => {
      if (!isMounted) return;
      
      // Load categories
      const categoryResponse = await categoryService.getCategories();
      if (isMounted && categoryResponse.success) {
        setCategories(categoryResponse.data);
      }

      // Load brands from the brands endpoint
      const brandsResponse = await brandService.getBrands();
      if (isMounted && brandsResponse.success && brandsResponse.data.length > 0) {
        const brands = brandsResponse.data.map(b => b.name).sort();
        setAvailableBrands(brands);
      } else if (isMounted) {
        // Fallback: Load first page of products to get some brands
        const productsResponse = await productService.getProducts({}, { limit: 100 });
        if (isMounted && productsResponse.success) {
          const allProducts = productsResponse.data.data;
          const brands = [...new Set(allProducts
            .map(product => product.brand || 'DemoCrackers')
            .filter(Boolean)
          )].sort();
          setAvailableBrands(brands);
        }
      }
    };
    loadCategoriesAndBrands();
    
    return () => {
      isMounted = false;
    };
  }, []);

  // Load products based on filters
  useEffect(() => {
    let isMounted = true;
    const abortController = new AbortController();
    
    const loadProducts = async () => {
      if (!isMounted) return;
      
      setIsLoading(true);
      try {
        const filterOptions = {
          category: filters.category.length > 0 ? filters.category[0] : undefined,
          brand: filters.brand.length > 0 ? filters.brand[0] : undefined,
          minPrice: filters.priceRange[0],
          maxPrice: filters.priceRange[1],
          inStock: filters.inStock ?? undefined,
          tags: filters.tags.length > 0 ? filters.tags : undefined,
          search: filters.searchQuery || undefined,
          sortBy: getSortOption(filters.sortBy)
        };

        const response = await productService.getProducts(filterOptions, {
          page: currentPage,
          limit: Math.min(itemsPerPage, 200) // Ensure we never exceed API limit
        });

        if (isMounted && response.success) {
          setProducts(response.data.data);
          setTotalPages(response.data.totalPages);
          setTotalProducts(response.data.total);
        }
      } catch (error) {
        if ((error as any).name !== 'AbortError') {
          console.error('Error loading products:', error);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadProducts();
    
    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [filters, currentPage, itemsPerPage]);

  const getSortOption = (sortBy: FilterState['sortBy']): FilterOptions['sortBy'] => {
    switch (sortBy) {
      case 'price-low': return 'price-asc';
      case 'price-high': return 'price-desc';
      case 'name': return 'name-asc';
      case 'newest': return 'newest';
      default: return undefined;
    }
  };

  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    
    filters.category.forEach(cat => params.append('category', cat));
    filters.brand.forEach(brand => params.append('brand', brand));
    if (filters.inStock !== null) params.set('inStock', filters.inStock.toString());
    filters.tags.forEach(tag => params.append('tags', tag));
    if (filters.searchQuery) params.set('search', filters.searchQuery);
    if (filters.sortBy !== 'featured') params.set('sort', filters.sortBy);
    
    // Add price range to query string
    if (filters.priceRange[0] !== 0) params.set('minPrice', filters.priceRange[0].toString());
    if (filters.priceRange[1] !== 10000) params.set('maxPrice', filters.priceRange[1].toString());
    
    setSearchParams(params);
  }, [filters, setSearchParams]);

  const handleFilterChange = <K extends keyof FilterState>(key: K, value: FilterState[K]) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    setCurrentPage(1); // Reset to first page when filters change
    
    // Track filter usage
    if (key === 'searchQuery' && value) {
      // Track search with debounce logic handled elsewhere
      const searchQuery = value as string;
      if (searchQuery.length > 2) {
        gtagService.trackSearch(searchQuery);
      }
    } else if (key === 'sortBy') {
      gtagService.trackSortUsage(value as string);
    } else if (key === 'category' || key === 'brand' || key === 'inStock' || key === 'priceRange') {
      gtagService.trackFilterUsage(key, String(value));
    }
  };

  const toggleCategory = (category: string) => {
    setFilters(prev => ({
      ...prev,
      category: prev.category.includes(category)
        ? prev.category.filter(c => c !== category)
        : [...prev.category, category]
    }));
    setCurrentPage(1);
    
    // Track category filter
    gtagService.trackFilterUsage('category', category);
  };

  const toggleBrand = (brand: string) => {
    setFilters(prev => ({
      ...prev,
      brand: prev.brand.includes(brand)
        ? prev.brand.filter(b => b !== brand)
        : [...prev.brand, brand]
    }));
    setCurrentPage(1);
    
    // Track brand filter
    gtagService.trackFilterUsage('brand', brand);
  };

  const clearFilters = () => {
    setFilters({
      category: [],
      brand: [],
      priceRange: [0, 10000],
      inStock: null,
      tags: [],
      searchQuery: '',
      sortBy: 'featured'
    });
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="w-full px-4 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-neutral-900 mb-2">All Products</h1>
          <p className="text-neutral-600">
            Browse our complete collection of premium fireworks
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Mobile Filter Toggle */}
          <button
            onClick={() => setIsFilterOpen(!isFilterOpen)}
            className="lg:hidden flex items-center justify-center gap-2 py-2 px-4 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            Filters
            {(filters.category.length > 0 || filters.brand.length > 0 || filters.inStock !== null || filters.tags.length > 0) && (
              <span className="bg-primary text-white text-xs px-2 py-1 rounded-full">
                {filters.category.length + filters.brand.length + (filters.inStock !== null ? 1 : 0) + filters.tags.length}
              </span>
            )}
          </button>

          {/* Filters Sidebar */}
          <aside className={`lg:w-64 ${isFilterOpen ? 'block' : 'hidden lg:block'}`}>
            <div className="bg-white rounded-xl shadow-sm p-6 sticky top-4">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold">Filters</h2>
                <button
                  onClick={clearFilters}
                  className="text-sm text-primary hover:text-primary-dark"
                >
                  Clear all
                </button>
              </div>

              {/* Search */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Search
                </label>
                <input
                  type="text"
                  value={filters.searchQuery}
                  onChange={(e) => handleFilterChange('searchQuery', e.target.value)}
                  placeholder="Search products..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>

              {/* Categories */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-neutral-700 mb-3">Categories</h3>
                <div className="space-y-2">
                  {categories.map(category => (
                    <label key={category.id} className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filters.category.includes(category.slug)}
                        onChange={() => toggleCategory(category.slug)}
                        className="rounded border-gray-300 text-primary focus:ring-primary"
                      />
                      <span className="ml-2 text-sm text-neutral-600">{category.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Brands */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-neutral-700 mb-3">Brands</h3>
                <div className="space-y-2">
                  {availableBrands.map(brand => (
                    <label key={brand} className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filters.brand.includes(brand)}
                        onChange={() => toggleBrand(brand)}
                        className="rounded border-gray-300 text-primary focus:ring-primary"
                      />
                      <span className="ml-2 text-sm text-neutral-600">{brand}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Price Range */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-neutral-700 mb-3">Price Range</h3>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm">₹</span>
                    <input
                      type="number"
                      value={filters.priceRange[0]}
                      onChange={(e) => handleFilterChange('priceRange', [parseInt(e.target.value), filters.priceRange[1]])}
                      className="w-20 px-2 py-1 text-sm border border-gray-300 rounded"
                      min="0"
                    />
                    <span className="text-sm">to</span>
                    <input
                      type="number"
                      value={filters.priceRange[1]}
                      onChange={(e) => handleFilterChange('priceRange', [filters.priceRange[0], parseInt(e.target.value)])}
                      className="w-20 px-2 py-1 text-sm border border-gray-300 rounded"
                      min="0"
                    />
                  </div>
                </div>
              </div>

              {/* Stock Status */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-neutral-700 mb-3">Availability</h3>
                <div className="space-y-2">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="stock"
                      checked={filters.inStock === null}
                      onChange={() => handleFilterChange('inStock', null)}
                      className="text-primary focus:ring-primary"
                    />
                    <span className="ml-2 text-sm text-neutral-600">All</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="stock"
                      checked={filters.inStock === true}
                      onChange={() => handleFilterChange('inStock', true)}
                      className="text-primary focus:ring-primary"
                    />
                    <span className="ml-2 text-sm text-neutral-600">In Stock</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="stock"
                      checked={filters.inStock === false}
                      onChange={() => handleFilterChange('inStock', false)}
                      className="text-primary focus:ring-primary"
                    />
                    <span className="ml-2 text-sm text-neutral-600">Out of Stock</span>
                  </label>
                </div>
              </div>

              {/* Featured */}
              <div className="mb-6">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.tags.includes('featured')}
                    onChange={(e) => {
                      const newTags = e.target.checked 
                        ? [...filters.tags, 'featured']
                        : filters.tags.filter(tag => tag !== 'featured');
                      handleFilterChange('tags', newTags);
                    }}
                    className="rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <span className="ml-2 text-sm text-neutral-600">Featured Products Only</span>
                </label>
              </div>
            </div>
          </aside>

          {/* Products Grid */}
          <div className="flex-1">
            {/* Sort and Results Count */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
              <p className="text-sm text-neutral-600">
                Showing {products.length} of {totalProducts} products
              </p>
              <select
                value={filters.sortBy}
                onChange={(e) => handleFilterChange('sortBy', e.target.value as FilterState['sortBy'])}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="featured">Featured</option>
                <option value="name">Name: A to Z</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="newest">Newest First</option>
              </select>
            </div>

            {/* Loading State */}
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-lg text-neutral-600">No products found matching your criteria.</p>
                <button
                  onClick={clearFilters}
                  className="mt-4 px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
                >
                  Clear Filters
                </button>
              </div>
            ) : (
              <>
                {/* Products Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
                  {products.map((product) => (
                    <ProductCard
                      key={product.id}
                      product={product}
                      onAddToCart={addToCart}
                    />
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="mt-8 flex justify-center">
                    <nav className="flex items-center gap-2">
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="px-3 py-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                      >
                        Previous
                      </button>
                      
                      {[...Array(totalPages)].map((_, index) => {
                        const page = index + 1;
                        if (
                          page === 1 ||
                          page === totalPages ||
                          (page >= currentPage - 1 && page <= currentPage + 1)
                        ) {
                          return (
                            <button
                              key={page}
                              onClick={() => handlePageChange(page)}
                              className={`px-3 py-2 rounded-lg ${
                                page === currentPage
                                  ? 'bg-primary text-white'
                                  : 'border border-gray-300 hover:bg-gray-50'
                              }`}
                            >
                              {page}
                            </button>
                          );
                        }
                        if (page === currentPage - 2 || page === currentPage + 2) {
                          return <span key={page} className="px-2">...</span>;
                        }
                        return null;
                      })}
                      
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="px-3 py-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                      >
                        Next
                      </button>
                    </nav>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Products;